/**
 * LitPet metadata helpers for the DFE Inventory arche's mint dialog.
 *
 * Deliberately thin. Unlike its Base Pet sibling (`base-pet-metadata.ts`), this
 * module does NOT rebuild the metadata contract client-side, because a LitPet's
 * blob is not something you can hand-write:
 *
 *   - the genome is 101 keys derived from 82 gene definitions across 390
 *     species (~9k lines of data in `@fias/litpet-data`), far too much to ship
 *     to the browser for an admin-only convenience;
 *   - the stats, rank, release and heptagen point are random rolls that must
 *     match what crafting produces, and a second implementation of those rolls
 *     would be free to drift from the real one — which is the exact failure the
 *     canonical-schema work was cleaning up.
 *
 * So the template is generated by the server
 * (`GET /v1/arches/dfe/admin/litpet-template`) using the same
 * `createGenotypeForSpecies` code path the crafting service uses, and this file
 * only holds what the dialog genuinely needs locally: recognising the name and
 * formatting the result.
 *
 * The prefix below mirrors `LITPET_NAME_PREFIX` in `@fias/db-types` (the client
 * cannot import that server-side package); `litpet-metadata.test.ts` pins the two
 * in lockstep, the same arrangement `base-pet-metadata.parity.test.ts` uses.
 */

/** Item-definition name prefix that identifies a LitPet, e.g. `LitPet - Aani`. */
export const LITPET_NAME_PREFIX = 'LitPet - ';

/** True when an item-definition name refers to a LitPet NFT. */
export function isLitPetDefinitionName(name: string | null | undefined): boolean {
  return typeof name === 'string' && name.startsWith(LITPET_NAME_PREFIX);
}

/** Species portion of a LitPet definition name, or `null` if it is not one. */
export function litPetSpeciesFromName(name: string | null | undefined): string | null {
  if (!isLitPetDefinitionName(name)) return null;
  return (name as string).slice(LITPET_NAME_PREFIX.length).trim() || null;
}

/** Pretty-print generated metadata for the dialog's textarea. */
export function formatLitPetMetadata(metadata: Record<string, unknown>): string {
  return JSON.stringify(metadata, null, 2);
}
